(() => {
    class AmaraBrandMarquee {
        constructor(section) {
            this.section = section;

            this.viewport = section.querySelector(
                '[data-amara-brand-viewport]'
            );

            this.track = section.querySelector(
                '[data-amara-brand-track]'
            );

            this.originalGroup = section.querySelector(
                '[data-amara-brand-group]'
            );

            if (
                !this.viewport ||
                !this.track ||
                !this.originalGroup
            ) {
                return;
            }

            this.baseSpeed =
                Number(section.dataset.speed) || 28;

            this.hoverSpeed =
                Number(section.dataset.hoverSpeed) || 3;

            this.direction =
                section.dataset.direction === 'right'
                    ? 1
                    : -1;

            this.currentSpeed =
                this.baseSpeed;

            this.targetSpeed =
                this.baseSpeed;

            this.position = 0;
            this.groupWidth = 0;

            this.lastTime = null;
            this.frame = null;

            this.isPointerDown = false;

            /*
             * True only while the current pointer gesture
             * has become an intentional horizontal drag.
             */
            this.hasDragged = false;

            /*
             * Suppresses the synthetic/native click that
             * immediately follows an actual drag.
             */
            this.suppressClick = false;

            this.pointerStartX = 0;
            this.pointerStartY = 0;

            this.lastPointerX = 0;

            /*
             * Prevent normal pointer jitter from being
             * interpreted as a drag.
             */
            this.dragThreshold = 10;

            /*
             * Horizontal movement should dominate before
             * we classify a gesture as a marquee drag.
             */
            this.horizontalIntentRatio = 1.15;

            this.reduceMotion =
                window.matchMedia(
                    '(prefers-reduced-motion: reduce)'
                );

            this.onFrame =
                this.onFrame.bind(this);

            this.onResize =
                this.debounce(
                    this.rebuild.bind(this),
                    180
                );

            this.onMouseEnter =
                this.onMouseEnter.bind(this);

            this.onMouseLeave =
                this.onMouseLeave.bind(this);

            this.onPointerDown =
                this.onPointerDown.bind(this);

            this.onPointerMove =
                this.onPointerMove.bind(this);

            this.onPointerUp =
                this.onPointerUp.bind(this);

            this.onClick =
                this.onClick.bind(this);

            this.onMotionPreferenceChange =
                this.onMotionPreferenceChange.bind(this);

            this.init();
        }


        /* =====================================================
           INITIALIZATION
           ===================================================== */

        init() {
            this.bindEvents();
            this.rebuild();
            this.updateMotionState();
        }


        bindEvents() {
            window.addEventListener(
                'resize',
                this.onResize,
                { passive: true }
            );

            this.section.addEventListener(
                'mouseenter',
                this.onMouseEnter
            );

            this.section.addEventListener(
                'mouseleave',
                this.onMouseLeave
            );

            this.viewport.addEventListener(
                'pointerdown',
                this.onPointerDown
            );

            this.viewport.addEventListener(
                'pointermove',
                this.onPointerMove
            );

            this.viewport.addEventListener(
                'pointerup',
                this.onPointerUp
            );

            this.viewport.addEventListener(
                'pointercancel',
                this.onPointerUp
            );

            /*
             * Capture phase is useful here because we need
             * to suppress anchor navigation only after
             * a genuine drag gesture.
             */
            this.viewport.addEventListener(
                'click',
                this.onClick,
                true
            );

            if (
                typeof this.reduceMotion.addEventListener ===
                'function'
            ) {
                this.reduceMotion.addEventListener(
                    'change',
                    this.onMotionPreferenceChange
                );
            } else {
                this.reduceMotion.addListener(
                    this.onMotionPreferenceChange
                );
            }
        }


        /* =====================================================
           MARQUEE BUILD / CLONING
           ===================================================== */

        rebuild() {
            this.stop();

            this.removeClones();

            this.position = 0;

            this.track.style.transform =
                'translate3d(0, 0, 0)';

            this.groupWidth =
                this.originalGroup
                    .getBoundingClientRect()
                    .width;

            if (!this.groupWidth) {
                return;
            }

            if (!this.reduceMotion.matches) {
                this.createClones();
                this.start();
            }
        }


        createClones() {
            const viewportWidth =
                this.viewport
                    .getBoundingClientRect()
                    .width;

            /*
             * Ensure enough repeated content exists so the
             * viewport never reveals empty space.
             */
            const minimumWidth =
                viewportWidth +
                this.groupWidth * 2;

            let renderedWidth =
                this.groupWidth;

            while (
                renderedWidth <
                minimumWidth
            ) {
                const clone =
                    this.originalGroup.cloneNode(true);

                clone.dataset.amaraBrandClone =
                    'true';

                clone.setAttribute(
                    'aria-hidden',
                    'true'
                );

                /*
                 * Cloned links must not duplicate keyboard
                 * tab stops.
                 */
                clone
                    .querySelectorAll('a, button')
                    .forEach((element) => {
                        element.setAttribute(
                            'tabindex',
                            '-1'
                        );
                    });

                /*
                 * Shopify block metadata should exist only
                 * on the original blocks.
                 */
                clone
                    .querySelectorAll(
                        '[data-shopify-editor-block]'
                    )
                    .forEach((element) => {
                        element.removeAttribute(
                            'data-shopify-editor-block'
                        );
                    });

                this.track.appendChild(
                    clone
                );

                renderedWidth +=
                    this.groupWidth;
            }
        }


        removeClones() {
            this.track
                .querySelectorAll(
                    '[data-amara-brand-clone]'
                )
                .forEach((clone) => {
                    clone.remove();
                });
        }


        /* =====================================================
           ANIMATION LOOP
           ===================================================== */

        start() {
            if (
                this.frame ||
                this.reduceMotion.matches
            ) {
                return;
            }

            this.lastTime =
                performance.now();

            this.frame =
                requestAnimationFrame(
                    this.onFrame
                );
        }


        stop() {
            if (!this.frame) {
                return;
            }

            cancelAnimationFrame(
                this.frame
            );

            this.frame = null;
            this.lastTime = null;
        }


        onFrame(time) {
            if (
                this.reduceMotion.matches
            ) {
                this.stop();
                return;
            }

            if (this.lastTime === null) {
                this.lastTime = time;
            }

            const deltaTime =
                Math.min(
                    (time - this.lastTime) / 1000,
                    0.05
                );

            this.lastTime = time;

            /*
             * Smooth velocity interpolation.
             *
             * This keeps hover slowdown / resume elegant
             * instead of snapping instantly between speeds.
             */
            const smoothing =
                1 -
                Math.pow(
                    0.025,
                    deltaTime
                );

            this.currentSpeed +=
                (
                    this.targetSpeed -
                    this.currentSpeed
                ) *
                smoothing;

            this.position +=
                this.currentSpeed *
                deltaTime *
                this.direction;

            this.normalizePosition();

            this.render();

            this.frame =
                requestAnimationFrame(
                    this.onFrame
                );
        }


        normalizePosition() {
            if (!this.groupWidth) {
                return;
            }

            while (
                this.position <=
                -this.groupWidth
            ) {
                this.position +=
                    this.groupWidth;
            }

            while (
                this.position >= 0
            ) {
                this.position -=
                    this.groupWidth;
            }
        }


        render() {
            this.track.style.transform =
                `translate3d(${this.position}px, 0, 0)`;
        }


        /* =====================================================
           HOVER
           ===================================================== */

        onMouseEnter() {
            if (
                window.matchMedia(
                    '(hover: hover)'
                ).matches
            ) {
                this.targetSpeed =
                    this.hoverSpeed;
            }
        }


        onMouseLeave() {
            if (!this.isPointerDown) {
                this.targetSpeed =
                    this.baseSpeed;
            }
        }


        /* =====================================================
           POINTER / DRAG INTERACTION
           ===================================================== */

onPointerDown(event) {
    if (
        event.button !== undefined &&
        event.button !== 0
    ) {
        return;
    }

    this.isPointerDown = true;

    this.hasDragged = false;
    this.suppressClick = false;

    this.pointerStartX = event.clientX;
    this.pointerStartY = event.clientY;
    this.lastPointerX = event.clientX;

    /*
     * Slow the automatic marquee while the
     * user is interacting.
     *
     * IMPORTANT:
     * Do NOT capture the pointer here.
     * A normal tap/click must remain owned
     * by the underlying anchor.
     */
    this.targetSpeed = 0;
}


onPointerMove(event) {
    if (!this.isPointerDown) {
        return;
    }

    const totalDeltaX =
        event.clientX -
        this.pointerStartX;

    const totalDeltaY =
        event.clientY -
        this.pointerStartY;

    const absX =
        Math.abs(totalDeltaX);

    const absY =
        Math.abs(totalDeltaY);

    /*
     * Only become a drag after clear
     * horizontal intent.
     */
    if (
        !this.hasDragged &&
        absX >= this.dragThreshold &&
        absX >
            absY *
            this.horizontalIntentRatio
    ) {
        this.hasDragged = true;

        /*
         * ONLY capture once a real drag
         * has actually begun.
         */
        try {
            this.viewport.setPointerCapture(
                event.pointerId
            );
        } catch (_) {
            /* Safe fallback */
        }
    }

    if (!this.hasDragged) {
        this.lastPointerX =
            event.clientX;

        return;
    }

    const deltaX =
        event.clientX -
        this.lastPointerX;

    this.position +=
        deltaX;

    this.normalizePosition();
    this.render();

    this.lastPointerX =
        event.clientX;
}


onPointerUp(event) {
    if (!this.isPointerDown) {
        return;
    }

    this.isPointerDown = false;

    const wasDragging =
        this.hasDragged;

    this.suppressClick =
        wasDragging;

    this.hasDragged =
        false;

    /*
     * Only release capture if a real drag
     * actually captured the pointer.
     */
    if (
        this.viewport.hasPointerCapture?.(
            event.pointerId
        )
    ) {
        try {
            this.viewport.releasePointerCapture(
                event.pointerId
            );
        } catch (_) {
            /* Safe fallback */
        }
    }

    this.currentSpeed =
        Math.min(
            this.currentSpeed,
            this.baseSpeed * 0.45
        );

    this.targetSpeed =
        this.baseSpeed;

    if (wasDragging) {
        window.setTimeout(
            () => {
                this.suppressClick = false;
            },
            160
        );
    }
}


        /* =====================================================
           CLICK / NAVIGATION
           ===================================================== */

        onClick(event) {
            /*
             * Normal clicks and taps should use the native
             * anchor href without interference.
             */
            if (!this.suppressClick) {
                return;
            }

            const link =
                event.target.closest(
                    'a[href]'
                );

            if (!link) {
                this.suppressClick = false;
                return;
            }

            /*
             * Only suppress the click immediately following
             * an actual horizontal drag.
             */
            event.preventDefault();
            event.stopPropagation();

            this.suppressClick = false;
        }


        /* =====================================================
           REDUCED MOTION
           ===================================================== */

        onMotionPreferenceChange() {
            this.updateMotionState();
        }


        updateMotionState() {
            if (
                this.reduceMotion.matches
            ) {
                this.stop();

                this.removeClones();

                this.position = 0;

                this.track.style.transform =
                    'none';

                return;
            }

            this.rebuild();
        }


        /* =====================================================
           CLEANUP
           ===================================================== */

        destroy() {
            this.stop();

            window.removeEventListener(
                'resize',
                this.onResize
            );

            this.section.removeEventListener(
                'mouseenter',
                this.onMouseEnter
            );

            this.section.removeEventListener(
                'mouseleave',
                this.onMouseLeave
            );

            this.viewport.removeEventListener(
                'pointerdown',
                this.onPointerDown
            );

            this.viewport.removeEventListener(
                'pointermove',
                this.onPointerMove
            );

            this.viewport.removeEventListener(
                'pointerup',
                this.onPointerUp
            );

            this.viewport.removeEventListener(
                'pointercancel',
                this.onPointerUp
            );

            this.viewport.removeEventListener(
                'click',
                this.onClick,
                true
            );

            if (
                typeof this.reduceMotion.removeEventListener ===
                'function'
            ) {
                this.reduceMotion.removeEventListener(
                    'change',
                    this.onMotionPreferenceChange
                );
            } else {
                this.reduceMotion.removeListener(
                    this.onMotionPreferenceChange
                );
            }
        }


        /* =====================================================
           UTILITIES
           ===================================================== */

        debounce(callback, delay) {
            let timer;

            return (...args) => {
                window.clearTimeout(
                    timer
                );

                timer =
                    window.setTimeout(
                        () => {
                            callback(...args);
                        },
                        delay
                    );
            };
        }
    }


    /* =========================================================
       INITIALIZATION
       ========================================================= */

    const instances =
        new WeakMap();


    const initSection = (section) => {
        if (
            !section ||
            instances.has(section)
        ) {
            return;
        }

        const instance =
            new AmaraBrandMarquee(
                section
            );

        instances.set(
            section,
            instance
        );
    };


    const initAll = (root = document) => {
        root
            .querySelectorAll(
                '[data-amara-brand-marquee]'
            )
            .forEach(
                initSection
            );
    };


    if (
        document.readyState ===
        'loading'
    ) {
        document.addEventListener(
            'DOMContentLoaded',
            () => initAll()
        );
    } else {
        initAll();
    }


    /* =========================================================
       SHOPIFY THEME EDITOR
       Reinitialize sections when Shopify dynamically loads them.
       ========================================================= */

    document.addEventListener(
        'shopify:section:load',
        (event) => {
            initAll(
                event.target
            );
        }
    );


    document.addEventListener(
        'shopify:section:unload',
        (event) => {
            const section =
                event.target.matches?.(
                    '[data-amara-brand-marquee]'
                )
                    ? event.target
                    : event.target.querySelector(
                        '[data-amara-brand-marquee]'
                    );

            if (!section) {
                return;
            }

            const instance =
                instances.get(
                    section
                );

            if (instance) {
                instance.destroy();

                instances.delete(
                    section
                );
            }
        }
    );
})();